import { InverterV2, ProductModelService } from "ngx-core-services";
import { decodeHistoryEventG1, decodeHistoryEventG2, decodeHistoryEventG3, decodePowerMagicEvent } from "ngx-modbus";

import { AlertContent, ProcessAlertsResult } from "./alert.model";
import { createDebugLogger, DebugConfig } from "../shared/debug";

export const ALERTS_DEBUG: DebugConfig = { ENABLED: false, LOG_DATA: false, LOG_STATE: false };
const Logger = createDebugLogger('[Alerts]', ALERTS_DEBUG);

export enum AlertCodeType {
  SOLARMAN = 'SOLARMAN',
  SOFAR = 'SOFAR',
}

export const g1ErrorMap: {[key: string]: number;} = {
  '96': 1,
  '97': 2,
  '98': 3,
  '99': 4,
  '89': 26,
  '6': 97
}

export const ZEErrorMap: {[key: string]: number;} = {
  '174': 1,
  '175': 2,
  '176': 3,
  '177': 4,
  '140': 33,
  '142': 35,
  '131': 52,
  '82': 102,
  '83': 103,
}

export const ZM1ErrorMap: {[key: string]: number;} = {
  '64': 1,
  '65': 2,
  '66': 3,
  '67': 4,
  '32': 33,
  '34': 35,
  '19': 52,
  '162': 102,
  '163': 103,
  '183': 85,
}

export const g1ErrorToShow: string[] = ['1', '2', '3', '4', '26', '97'];

export const ZM1ErrorToShow: string[] = ['1', '2', '3', '4', '33', '35', '52', '85', '102', '103'];

export const ZEErrorToShow: string[] = ['1', '2', '3', '4', '33', '35', '52', '102', '103'];

export const v3ErrorToShow: string[] = ['1', '2', '3', '4', '42', '45', '47', '69', '152', '156', '157', '158'];

export const invertV1ProductTypes = ['A', 'B'];

export const inverterV3ProductTypes = ['D'];

export const convertIdToCode = (id: string) => {
  return 'ID' + id.padStart(3, '0');
}

export const convertCodeToId = (code: string) => {
  return code.replace('ID', '').replace(/^0+/, '');
}

interface DecodeAlertOptions {
  showAll?: boolean;
}

export const processAndGroupAlerts = (
  telemetry: { [serial: string]: Partial<InverterV2>[] },
  options: DecodeAlertOptions = {},
): ProcessAlertsResult => {
  const errorsByCode: AlertContent = {};
  const allTimestampsBySerial: { [serial: string]: number[] } = {};

  Logger.info('processAndGroupAlerts called', { serialCount: Object.keys(telemetry).length, serials: Object.keys(telemetry), showAll: options.showAll });

  for (const serial in telemetry) {
    if (telemetry.hasOwnProperty(serial)) {
      const telemetryData = telemetry[serial];

      Logger.data(`serial=${serial}, records=${telemetryData.length}`, telemetryData.map(e => ({ TS: e.TS, GwId: e.GwId, ALERTS: e.ALERTS, ALERTS_ZCS: e.ALERTS_ZCS })));

      // Extract all timestamps for this serial (sorted)
      allTimestampsBySerial[serial] = telemetryData
        .filter((element) => element.TS !== undefined)
        .map((element) => element.TS!)
        .sort((a, b) => a - b);

      telemetryData.forEach((element) => {
        // * The alarm type is determined based on the ID of the gateway that sent the telemetry
        const gwId = element.GwId;
        if (!gwId) {
          Logger.state(`SKIP serial=${serial} — no GwId`, { TS: element.TS, ALERTS: element.ALERTS, ALERTS_ZCS: element.ALERTS_ZCS });
          return;
        }
        const gwProduct = new ProductModelService(gwId);
        const alertType = gwProduct.type?.name === 'azzurro-hub' ? AlertCodeType.SOFAR : AlertCodeType.SOLARMAN;
        const alertProperty = alertType === AlertCodeType.SOFAR ? 'ALERTS_ZCS' : 'ALERTS';

        Logger.state(`serial=${serial}, GwId=${gwId}, gwType=${gwProduct.type?.name}, alertType=${alertType}, property=${alertProperty}, value=${element[alertProperty]}`);

        if (element[alertProperty]) {
          element[alertProperty]?.split(',').forEach((alert) => {
            // * for Solarman alerts, we need to map them to Sofar alert IDs
            const sofarId = alertType === AlertCodeType.SOLARMAN ? mapSolarmanAlertToSofarAlert(serial, alert.trim()) : Number(alert.trim());
            if (!sofarId) {
              Logger.state(`SKIP serial=${serial}, rawAlert=${alert.trim()} — mapSolarmanAlertToSofarAlert returned undefined (alertType=${alertType})`);
              return;
            }

            const decodedAlert = decodeAlert(serial, sofarId, options);
            Logger.state(`serial=${serial}, rawAlert=${alert.trim()}, sofarId=${sofarId}, decoded=${!!decodedAlert}`, decodedAlert ? { faultCode: decodedAlert.faultCode } : 'FILTERED OUT');
            if (decodedAlert) {
              if (!errorsByCode[decodedAlert.faultCode]) {
                errorsByCode[decodedAlert.faultCode] = {
                  serials: {},
                  originalCode: sofarId.toString(),
                  details: {
                    ...decodedAlert.error,
                    faultCode: decodedAlert.faultCode,
                  },
                };
              }
              if (!errorsByCode[decodedAlert.faultCode].serials[serial]) {
                errorsByCode[decodedAlert.faultCode].serials[serial] = [];
              }
              errorsByCode[decodedAlert.faultCode].serials[serial].push({
                TS: element.TS,
                alerts: [{ faultCode: decodedAlert.faultCode, ...decodedAlert.error }]
              });
            }
          });
        }
      });
    }
  }

  const totalCount = Object.values(errorsByCode).reduce((total, alert) => total + Object.keys(alert.serials || {}).length, 0);
  Logger.info('processAndGroupAlerts result', { alertCodes: Object.keys(errorsByCode), totalCount, details: Object.entries(errorsByCode).map(([code, a]) => ({ code, serials: Object.keys(a.serials) })) });

  return { alerts: errorsByCode, allTimestampsBySerial };
};

export const mapSolarmanAlertToSofarAlert = (deviceId: string, alert: string): number | undefined => {
  const inverter = new ProductModelService(deviceId);
  if ( inverter.id.startsWith('ZE') ) {
    return ZEErrorMap[alert];
  }

  if( inverter.id.startsWith('ZM1') ) {
    return ZM1ErrorMap[alert];
  }

  if ( inverter.type?.name === 'inverter' && invertV1ProductTypes.includes(inverter.inverterProductType!) ) {
    return g1ErrorMap[alert];
  }

  if ( inverter.type?.name === 'inverter' && inverterV3ProductTypes.includes(inverter.inverterProductType!) ) {
    return Number(alert) + 1;
  }

  return;
};

export const decodeAlert = (device: string, alert: number, options: DecodeAlertOptions = {}) => {
  const inverter = new ProductModelService(device);
  const showAll = options.showAll === true;

  if( inverter.isPowerMagic ) {
    return {
      faultCode: convertIdToCode(alert.toString()),
      error: decodePowerMagicEvent(alert).feature!,
    }
  }

  if ( inverter.id.startsWith('ZE') ||  inverter.id.startsWith('ZM1') ) {
    const isZEAlertToShow = inverter.id.startsWith('ZE') && ZEErrorToShow.includes(alert.toString());

    const isZM1AlertToShow = inverter.id.startsWith('ZM1') && ZM1ErrorToShow.includes(alert.toString());

    if (showAll || isZEAlertToShow || isZM1AlertToShow) {
      return {
        faultCode: convertIdToCode(alert.toString()),
        error: decodeHistoryEventG2(alert).feature!,
      };
    }
  }

  if ( inverter.type?.name === 'inverter' && invertV1ProductTypes.includes(inverter.inverterProductType!) ) {
    if ( showAll || g1ErrorToShow.includes(alert.toString()) ) {
      return {
        faultCode: convertIdToCode(alert.toString()),
        error: decodeHistoryEventG1(alert).feature!,
      };
    }
  }

  if ( inverter.type?.name === 'inverter' && inverterV3ProductTypes.includes(inverter.inverterProductType!) ) {
    if ( showAll || v3ErrorToShow.includes(alert.toString()) ) {
      return {
        faultCode: convertIdToCode(alert.toString()),
        error: decodeHistoryEventG3(Math.floor(alert / 16) + 1, (alert % 16) - 1).feature!,
      };
    }
  }

  return;
}
